/*
 * CONTACTS PAGE
 *
 * Provides contact list/table views, filters, schema selection, export, and contact creation.
 * Recent changes: Consolidated synchronized contact browsing under RLP Contact,
 * hiding Franchise Contact as a separate list choice while preserving Overview access.
 */

import { FC, useEffect, useState, useRef, useCallback } from 'react';
import { THEME_CLASSES } from '../constants/themeConstants';
import { SchemaProvider, type Schema } from '../context/SchemaContext';
import NewContact from '../components/contact/NewContact';
import useDebounce from '../hooks/useDebounce';
import ContactList from '../components/contact/ContactList';
import ContactTable from '../components/contact/ContactTable';
import SchemaSelector from '../components/common/SchemaSelector';
import ExportButton from '../components/common/ExportButton';
import { useLocation, useNavigate } from 'react-router';
import { useTeams } from '../context/TeamContext';
import { Contact } from '../models/Contact';
import Spinner from '../components/common/Spinner';
import PageLayout from '../components/patterns/PageLayout';
import { Button } from '../components/primitives';

export const canViewContactSchema = (
  schema: Schema,
  teams: ReadonlyArray<{ teamId: number }>,
) => {
  const normalizedName = schema.name.trim().toLowerCase();

  if (normalizedName === 'franchise contact') {
    return false;
  }

  return normalizedName === 'rlp contact'
    || teams.some((team) => team.teamId === schema.teamId);
};

// Inner component that uses SchemaContext
const ContactsContent: FC = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const activeTab: 'all' | 'my' = pathname.endsWith('/all') ? 'all' : 'my';
  const { teams, isLoading: isLoadingUser } = useTeams();

  const contactSchemaFilter = useCallback(
    (schema: Schema) => canViewContactSchema(schema, teams),
    [teams],
  );

  // Check for create=true query parameter
  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get('create') === 'true') {
      setIsNewContactOpen(true);
    }
  }, [search]);

  const [isNewContactOpen, setIsNewContactOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(() => localStorage.getItem('contacts-search') || '');
  const [selectedSchema, setSelectedSchema] = useState<Schema | null>(null);
  const [rowCount, setRowCount] = useState(0);

  // Export functionality
  const [exportData, setExportData] = useState<Contact[]>([]);
  const [exportColumns, setExportColumns] = useState<Array<{field: string, headerName: string}>>([]);

  // Stable callback functions to prevent unnecessary re-renders
  const handleExportDataChange = useCallback((data: Contact[], columns: Array<{field: string, headerName: string}>) => {
    setExportData(data);
    setExportColumns(columns);
  }, []);

  const handleClose = () => {
    // Remove query parameters while keeping the path
    const url = new URL(window.location.href);
    url.searchParams.delete('create');
    url.searchParams.delete('email');
    url.searchParams.delete('name');
    navigate(pathname + url.search);

    setIsNewContactOpen(false);
  };

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const contactCountLabel = `${rowCount} ${rowCount === 1 ? 'contact' : 'contacts'}`;

  // Handle initial tab restoration on mount
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      const storedTab = localStorage.getItem('contacts-tab');
      if (storedTab && storedTab !== activeTab) {
        navigate(storedTab === 'all' ? '/contacts/all' : '/contacts/my');
      }
      isInitialMount.current = false;
    }
  }, [navigate, activeTab]);

  // Persist search in localStorage
  useEffect(() => {
    localStorage.setItem('contacts-search', searchQuery);
    localStorage.setItem('contacts-tab', activeTab);
  }, [searchQuery, activeTab]);

  if (isLoadingUser) {
    return (
      <div className={`p-6 ${THEME_CLASSES.text.primary}`}>
        <div className="flex items-center justify-center h-64">
          <Spinner />
          <span className="ml-3">Loading user data...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageLayout>
        {/* header */}
        <div className="mx-4 flex flex-col gap-3 sm:mx-6 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap items-center gap-3 xl:shrink-0 xl:flex-nowrap">
            <h1 className={`text-xl font-semibold my-auto ${THEME_CLASSES.text.primary}`}>Contacts</h1>
            {activeTab === 'all' && (
              <Button
                onClick={() => setIsNewContactOpen(true)}
                size="sm"
                className="min-h-9"
                aria-label="New contact"
                leftIcon={(
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 3a1 1 0 0 1 1 1v5h5a1 1 0 1 1 0 2h-5v5a1 1 0 1 1-2 0v-5H4a1 1 0 1 1 0-2h5V4a1 1 0 0 1 1-1z" clipRule="evenodd" />
                  </svg>
                )}
              >
                <span className="xl:hidden 2xl:inline">New contact</span>
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 xl:shrink-0 xl:flex-nowrap">
            <div className={`inline-flex h-9 rounded-xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} p-1 shadow-sm`}>
              <button
                type="button"
                onClick={() => {
                  localStorage.setItem('contacts-tab', 'my');
                  navigate('/contacts/my');
                }}
                className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1 text-sm font-medium transition-colors ${activeTab === 'my'
                  ? `${THEME_CLASSES.button.primary} shadow-sm`
                  : `${THEME_CLASSES.text.secondary} hover:bg-slate-100 dark:hover:bg-slate-900`
                  }`}
              >
                My Contacts
              </button>
              <button
                type="button"
                onClick={() => {
                  localStorage.setItem('contacts-tab', 'all');
                  navigate('/contacts/all');
                }}
                className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1 text-sm font-medium transition-colors ${activeTab === 'all'
                  ? `${THEME_CLASSES.button.primary} shadow-sm`
                  : `${THEME_CLASSES.text.secondary} hover:bg-slate-100 dark:hover:bg-slate-900`
                  }`}
              >
                All Contacts
              </button>
            </div>
            <div className="shrink-0 whitespace-nowrap rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300" aria-live="polite">
              {contactCountLabel}
            </div>
            {activeTab === 'all' && selectedSchema && (
              <div id="grid-column-selector-portal" className="inline-flex xl:hidden 2xl:inline-flex" />
            )}
          </div>
          <div className="relative grid w-full grid-cols-1 gap-2 sm:grid-cols-2 xl:w-auto xl:shrink-0 xl:grid-cols-[13rem_12rem_auto] xl:items-center 2xl:grid-cols-[15rem_12rem_auto]">
            {/* Search box */}
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className={`h-4 w-4 ${THEME_CLASSES.text.secondary}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM2 8a6 6 0 1 1 10.89 3.476l4.817 4.817a1 1 0 0 1-1.414 1.414l-4.816-4.816A6 6 0 0 1 2 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-11 pr-8 py-2 border rounded-md text-xs font-medium focus:outline-none focus:ring-1 focus:ring-blue-500 ${searchQuery ? 'border-red-500' : THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} ${THEME_CLASSES.input.placeholder}`}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  aria-label="Clear contact search"
                >
                  <svg className={`h-4 w-4 ${THEME_CLASSES.text.secondary} hover:${THEME_CLASSES.text.primary}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 1 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414l-4.293 4.293 4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414l4.293-4.293-4.293-4.293a1 1 0 0 1 0-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
            {/* Schema Selector */}
            {activeTab === 'all' && (
              <SchemaSelector
                onSchemaChange={setSelectedSchema}
                entityType={1} /* 1 is for contacts */
                label="Type"
                placeholder="Select contact type"
                ariaLabel="Select contact type"
                className="w-full"
                storageKey="contacts-team"
                schemaFilter={contactSchemaFilter}
              />
            )}
            {/* Export Button */}
            {activeTab === 'all' && (
              <ExportButton
                data={exportData}
                columns={exportColumns}
                filename={selectedSchema ?
                  `contacts-${selectedSchema.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv` :
                  `contacts-${new Date().toISOString().split('T')[0]}.csv`}
                disabled={!selectedSchema || exportData.length === 0}
              />
            )}
          </div>
        </div>

        {/* main area */}
        <div className="relative min-h-0 flex-1 overflow-y-auto">
          {activeTab === 'all' ? (
            selectedSchema ? (
              <ContactTable
                searchQuery={debouncedSearchQuery}
                schema={selectedSchema}
                onRowCountChange={setRowCount}
                onDataChange={handleExportDataChange}
                onClearSearch={() => setSearchQuery('')}
                onCreateContact={() => setIsNewContactOpen(true)}
              />
            ) : (
              <div className={`flex h-64 flex-col items-center justify-center gap-3 px-6 text-center ${THEME_CLASSES.text.secondary}`}>
                <h2 className={`text-base font-semibold ${THEME_CLASSES.text.primary}`}>Select a contact type to view contacts</h2>
                <p className="max-w-md text-sm">Use the Contact type selector above to choose the contact list you want to review.</p>
              </div>
            )
          ) : (
            <ContactList
              searchValue={debouncedSearchQuery}
              onRowCountChange={setRowCount}
              onBrowseAllContacts={() => navigate('/contacts/all')}
              onClearSearch={() => setSearchQuery('')}
            />
          )}
        </div>
      </PageLayout>
      <NewContact
        isOpen={isNewContactOpen}
        onClose={handleClose}
        initialEmail={new URLSearchParams(search).get('email') || ''}
        initialName={new URLSearchParams(search).get('name') || ''}
      />
    </>
  );
};

const Contacts: FC = () => {
  return (
    <SchemaProvider>
      <ContactsContent />
    </SchemaProvider>
  );
};

export default Contacts;
